import type { HelmInspectQuery } from '@features/fields/api/useHelmInspect'
import { HeadlandCornerType } from 'api-validations/locutus/guidance'
import { useMemo } from 'react'
import { type UseFormReturn, useWatch } from 'react-hook-form'
import { useLocalization } from 'shared-ui/localization'
import { planZoneColor, useMapContext, useSelectedId } from 'shared-ui/map'
import { Geo } from 'utils'

import type { FormValues } from './formtypes'
import { cornerKey, withOverride } from './overrideKeys'
import {
  InspectStates,
  OverrideRow,
  type PillOption,
  PillToggle,
  useCorridorBankEdgeIds,
  usePassableHolePolygons,
  useViewOnlyFeatures
} from './shared'

export const useCornerOptions = (): readonly [
  PillOption<HeadlandCornerType>,
  PillOption<HeadlandCornerType>
] => {
  const { t } = useLocalization()
  return [
    {
      value: HeadlandCornerType.Round,
      label: t(($) => $.planning.cornerTypes.Round),
      subtleColor: 'bg-gray-100 text-gray-700',
      strongColor: 'bg-accent-100 text-green-700'
    },
    {
      value: HeadlandCornerType.Square,
      label: t(($) => $.planning.cornerTypes.Square),
      subtleColor: 'bg-gray-100 text-gray-700',
      strongColor: 'bg-accent-100 text-green-700'
    }
  ]
}

export const Corners = ({
  form,
  inspectQuery
}: {
  form: UseFormReturn<FormValues>
  inspectQuery: HelmInspectQuery
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()
  const corners = inspectQuery.data?.corners ?? []
  const holes = inspectQuery.data?.holes ?? []
  const corridors = inspectQuery.data?.corridors ?? []
  const cornerOptions = useCornerOptions()

  const selectedCornerId = useSelectedId()

  const cornerOverrides = useWatch({ control: form.control, name: 'cornerOverrides' })
  const setCornerType = (key: string, type: HeadlandCornerType | null) =>
    form.setValue(
      'cornerOverrides',
      withOverride(cornerOverrides, key, type === null ? undefined : { key, cornerType: type }),
      { shouldDirty: true }
    )

  const passableHolePolygons = usePassableHolePolygons(form, holes)
  const corridorBankEdgeIds = useCorridorBankEdgeIds(form, corridors)
  const hiddenCornerIds = useMemo(() => {
    const set = new Set<string>()
    for (const corner of corners) {
      if (Geo.featureOverlapsAnyPolygon(corner.feature, passableHolePolygons)) set.add(corner.id)
      // A corner on a corridor bank (the bank's ends where it meets the
      // fence included) disappears when the regions meld across the strip.
      if (corner.edges.some((id) => corridorBankEdgeIds.has(id))) set.add(corner.id)
    }
    return set
  }, [corners, passableHolePolygons, corridorBankEdgeIds])

  const visibleCorners = useMemo(
    () => corners.filter((c) => !hiddenCornerIds.has(c.id)),
    [corners, hiddenCornerIds]
  )

  useViewOnlyFeatures('corner', visibleCorners, (c) => ({
    color: planZoneColor(corners.findIndex((x) => x.id === c.id))
  }))

  return (
    <InspectStates
      query={inspectQuery}
      isEmpty={corners.length === 0}
      emptyText={t(($) => $.planning.noCornersDetected)}
    >
      <div className="flex flex-col gap-3 py-4">
        <div className="flex items-center justify-between">
          <div className="pan-title5">{t(($) => $.planning.corners)}</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {corners.map((corner, cornerIndex) => {
            const key = cornerKey(corner)
            const override = cornerOverrides.find((o) => o.key === key)
            if (override === undefined) return null
            if (hiddenCornerIds.has(corner.id)) return null
            const isSelected = corner.id === selectedCornerId
            return (
              <OverrideRow
                key={corner.id}
                color={planZoneColor(cornerIndex)}
                label={t(($) => $.planning.cornerNumber, { number: cornerIndex + 1 })}
                isSelected={isSelected}
                onSelect={() => map.drawTool.setSelected(isSelected ? null : corner.id)}
                isOverridden
                onReset={() => setCornerType(key, null)}
              >
                <PillToggle
                  value={override.cornerType}
                  options={cornerOptions}
                  onChange={(v) => setCornerType(key, v)}
                />
              </OverrideRow>
            )
          })}
        </div>
      </div>
    </InspectStates>
  )
}
